/* Escopo de dados do usuário logado.

   Resolve a pendência P6 do plano — e sem precisar de nada do backend. O AVA já
   guarda o escopo no Redux desde o login: cada entrada de `user.user_roles`
   traz `role_id`, `group_id`, `region_id` e `school_id`.

   A mesma leitura é feita pelo `checkUserRoles()` de
   pages/Consolidated/Panel/filter.jsx, que usa esses campos para filtrar as
   listas de grupo/região/escola. Aqui a lógica está centralizada e nomeada.

   Um usuário pode ter vários VÍNCULOS do mesmo papel (ex.: professor em duas
   escolas). Quando os valores divergem entre eles, devolvemos null no campo —
   significa "mais de um", e a tela precisa deixar o usuário escolher em vez de
   assumir.

   O que NÃO acontece mais desde 04/08/2026: misturar vínculos de papéis
   diferentes. O escopo sai só dos vínculos do papel ATIVO — ver a nota em
   config/perfilPorRole.js.

   ---------------------------------------------------------------------------
   A ESCOLHA NO AVA VENCE O VÍNCULO
   ---------------------------------------------------------------------------
   O vínculo não é a última palavra: o AVA tem o modal "Escolha sua rede"
   (components/Modal/ModalGroupRegion), que grava grupo/região/escola em
   `general.active_roles` — preservando o `role_id`. É onde o usuário diz em qual
   rede está trabalhando agora, então é o que o painel tem de obedecer.

   Sem isso, o Administrador Educandus não tinha painel NENHUM: o vínculo dele é
   vazio de propósito (ele é irrestrito), e medido em 04/08/2026 são 33 de 33
   vínculos de tipo G com `group_id` NULL. O painel do gestor lia só o vínculo,
   achava null e respondia "Não foi possível identificar a sua rede" — mesmo
   depois de o usuário escolher a rede no modal.

   Segurança: esses ids vêm do cliente e são apenas RECORTE DE INTERFACE. Quem
   autoriza é o backend, que valida o recorte pedido contra os vínculos do token
   (EscopoAnalyticsService::aplicarRecorteSolicitado) — um papel que forjasse
   grupo alheio no Redux tomaria 403. */

import { PERFIL_POR_ROLE } from "../config/perfilPorRole";

const unico = valores => {
  const distintos = [
    ...new Set(valores.filter(v => v !== null && v !== undefined && v !== 0))
  ];
  return distintos.length === 1 ? distintos[0] : null;
};

/** O que o usuário escolheu no AVA, se escolheu; senão o que o vínculo diz.

    `null` no active_roles é resposta legítima e não um "não sei": é o "Todos" do
    modal. Cai para o vínculo justamente porque, para quem TEM vínculo, "Todos"
    não amplia nada — o backend recorta pelo token de todo modo. Para o
    Administrador Educandus os dois são null, e null ali significa "todas as
    redes". */
const escolhidoOuDoVinculo = (escolhido, doVinculo) =>
  escolhido !== null && escolhido !== undefined && escolhido !== 0
    ? escolhido
    : doVinculo;

/**
 * @param {Array} userRoles  user.user_roles do Redux
 * @param {Object} activeRoles  general.active_roles (role_id + grupo/região/escola
 *                              escolhidos no modal do AVA)
 * @returns {{grupoId:number|null, regiaoId:number|null, escolaId:number|null,
 *            grupoIds:number[], regiaoIds:number[], escolaIds:number[],
 *            escopo:string|null}}
 */
export const escopoDoUsuario = (userRoles = [], activeRoles = null) => {
  const roles = Array.isArray(userRoles) ? userRoles : [];
  const activeRoleId = activeRoles ? activeRoles.role_id : null;

  // Só os vínculos do papel ATIVO. Antes havia um fallback para "todos os
  // papéis elegíveis" quando o papel ativo não tinha vínculo na lista, e ele
  // recortava as chamadas pelo grupo/escola de OUTRO papel — o mesmo engano que
  // a tela de seleção de visão cometia, agora no recorte dos dados.
  const base = roles.filter(r => r.role_id === activeRoleId);

  const grupoIds = [...new Set(base.map(r => r.group_id).filter(Boolean))];
  const regiaoIds = [...new Set(base.map(r => r.region_id).filter(Boolean))];
  const escolaIds = [...new Set(base.map(r => r.school_id).filter(Boolean))];

  const doVinculo = {
    grupoId: unico(base.map(r => r.group_id)),
    regiaoId: unico(base.map(r => r.region_id)),
    escolaId: unico(base.map(r => r.school_id))
  };

  return {
    grupoId: escolhidoOuDoVinculo(
      activeRoles && activeRoles.group_id,
      doVinculo.grupoId
    ),
    regiaoId: escolhidoOuDoVinculo(
      activeRoles && activeRoles.region_id,
      doVinculo.regiaoId
    ),
    escolaId: escolhidoOuDoVinculo(
      activeRoles && activeRoles.school_id,
      doVinculo.escolaId
    ),
    grupoIds,
    regiaoIds,
    escolaIds,
    escopo: PERFIL_POR_ROLE[activeRoleId]
      ? PERFIL_POR_ROLE[activeRoleId].escopo
      : null
  };
};

/**
 * Parâmetros de recorte para as chamadas de API, conforme o escopo do papel.
 *
 * Um administrador de regional (papel 3) precisa enviar `region_id` em toda
 * chamada — sem isso veria a rede inteira. O painel do Gestor já opera nesse
 * escopo (ver D3.1 do plano), então basta alimentar os parâmetros.
 *
 * ⚠️ Isto é recorte de INTERFACE. Na Etapa 2 o backend do AVA precisa impor o
 * mesmo recorte no servidor: as rotas /escola/:id/inativos, /aluno/:id/execucao
 * e /aluno/:id/tempo-online devolvem dados nominais de alunos.
 */
export const recorteDoEscopo = escopo => {
  const p = {};
  if (!escopo) return p;
  if (escopo.grupoId) p.grupo_id = escopo.grupoId;
  if (escopo.escopo === "regiao" && escopo.regiaoId)
    p.region_id = escopo.regiaoId;
  if (escopo.escopo === "escola" && escopo.escolaId)
    p.escola_id = escopo.escolaId;
  return p;
};
